import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  model,
  signal,
  viewChild,
  type ElementRef,
} from '@angular/core';
import {
  SegmentedControllerComponent,
  type SegmentedControllerOption,
} from '../../molecules/segmented-controller/segmented-controller.component';
import { TextDirective } from '../../typography/text.directive';

const DEVICE_PRESET_IDS = ['mobile', 'tablet', 'desktop'] as const;
export type DevicePresetId = (typeof DEVICE_PRESET_IDS)[number];

export interface DevicePreset {
  readonly id: DevicePresetId;
  readonly label: string;
  readonly width: number;
  readonly height: number;
}

const DEVICE_PRESETS: readonly DevicePreset[] = [
  { id: 'mobile', label: 'Mobile', width: 390, height: 844 },
  { id: 'tablet', label: 'Tablet', width: 768, height: 1024 },
  { id: 'desktop', label: 'Desktop', width: 1280, height: 800 },
] as const;

const PRESET_BY_ID = new Map<DevicePresetId, DevicePreset>(DEVICE_PRESETS.map((p) => [p.id, p]));

interface DeviceChromeMetrics {
  readonly bezelTop: number;
  readonly bezelRight: number;
  readonly bezelBottom: number;
  readonly bezelLeft: number;
}

const NO_CHROME: DeviceChromeMetrics = {
  bezelTop: 0,
  bezelRight: 0,
  bezelBottom: 0,
  bezelLeft: 0,
};

const DEVICE_CHROME: Record<DevicePresetId, DeviceChromeMetrics> = {
  mobile: {
    bezelTop: 48,
    bezelRight: 14,
    bezelBottom: 20,
    bezelLeft: 14,
  },
  tablet: {
    bezelTop: 32,
    bezelRight: 32,
    bezelBottom: 32,
    bezelLeft: 32,
  },
  desktop: {
    bezelTop: 24,
    bezelRight: 18,
    bezelBottom: 18,
    bezelLeft: 18,
  },
};

function isDevicePresetId(value: string): value is DevicePresetId {
  return (DEVICE_PRESET_IDS as readonly string[]).includes(value);
}

/** Matches `--device-preview-duration` in `device-preview.component.css` (preset-only transitions). */
const PRESET_TRANSITION_MS = 480;

/** Minimum visible scaled device height when a bottom inset is near-max (px). */
const MIN_SCALED_DEVICE_H = 160;

let nextToolbarLabelId = 0;

@Component({
  selector: 'joii-device-preview',
  templateUrl: './device-preview.component.html',
  styleUrl: './device-preview.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SegmentedControllerComponent, TextDirective],
  host: {
    '[class.device-preview-preset-transition]': 'presetTransitionActive()',
    '[attr.data-device-chrome]': 'chromeVisible() ? "true" : "false"',
    '[attr.data-scale-to-fit]': 'scaleToFit() ? "true" : "false"',
  },
})
export class DevicePreviewComponent {
  /**
   * Extra space (px) reserved at the bottom of the stage, e.g. a mobile bottom sheet.
   * Scale-to-fit keeps at least 160px of the scaled device visible.
   */
  readonly bottomInset = input(0);

  /** When true, draws phone, tablet, or laptop chrome around the preview. */
  readonly deviceChrome = input(false, { transform: booleanAttribute });

  /** When true, the device is scaled to fit the stage. When false, it renders at 1:1. */
  readonly scaleToFit = input(true, { transform: booleanAttribute });

  /** Current device preset. */
  readonly selectedDeviceId = model<DevicePresetId>('mobile');

  protected readonly toolbarLabelId = `joii-device-preview-toolbar-label-${nextToolbarLabelId++}`;

  /** Segmented controller options (static). */
  protected readonly deviceOptions: SegmentedControllerOption[] = DEVICE_PRESETS.map((p) => ({
    label: p.label,
    value: p.id,
  }));

  /** When true, width/height/scale transitions run (only after a device preset change). */
  protected readonly presetTransitionActive = signal(false);

  /** Measured width of the preview stage (content box). */
  protected readonly stageWidth = signal(0);

  /** Measured height of the preview stage (content box). */
  protected readonly stageHeight = signal(0);

  /** Measured height of the scale footer (content box). */
  protected readonly footerHeight = signal(0);

  /** Row gap between viewport and scale footer in `.preview-device-column`. */
  protected readonly columnGap = signal(0);

  protected readonly selectedDevice = computed(() => PRESET_BY_ID.get(this.selectedDeviceId()) ?? DEVICE_PRESETS[0]);

  protected readonly chromeVisible = computed(() => this.deviceChrome() && !this.presetTransitionActive());

  protected readonly chrome = computed(() =>
    this.deviceChrome() ? DEVICE_CHROME[this.selectedDeviceId()] : NO_CHROME
  );

  protected readonly shellWidth = computed(() => {
    const { width } = this.selectedDevice();
    const chrome = this.chrome();
    return width + chrome.bezelLeft + chrome.bezelRight;
  });

  protected readonly shellHeight = computed(() => {
    const { height } = this.selectedDevice();
    const chrome = this.chrome();
    return height + chrome.bezelTop + chrome.bezelBottom;
  });

  private readonly effectiveBottomInset = computed(() => {
    const stageH = this.stageHeight();
    if (stageH <= 0) {
      return 0;
    }
    const footer = this.footerHeight();
    const gap = this.columnGap();
    const scaledDeviceMin = MIN_SCALED_DEVICE_H + footer + gap;
    const maxInset = Math.max(0, stageH - scaledDeviceMin);
    return Math.min(this.bottomInset(), maxInset);
  });

  /** Reserves space for a bottom overlay so vertical centering stays above it. */
  protected readonly stageBottomPadding = this.effectiveBottomInset;

  protected readonly scale = computed(() => {
    if (!this.scaleToFit()) {
      return 1;
    }
    const stageW = this.stageWidth();
    const stageH = this.stageHeight();
    const shellW = this.shellWidth();
    const shellH = this.shellHeight();
    if (stageW <= 0 || shellW <= 0) {
      return 1;
    }
    const byWidth = stageW / shellW;
    if (stageH <= 0 || shellH <= 0) {
      return byWidth;
    }
    const footer = this.footerHeight();
    const gap = this.columnGap();
    const effectiveInset = this.effectiveBottomInset();
    const availableH = Math.max(0, stageH - footer - gap - effectiveInset);
    if (availableH <= 0) {
      return byWidth;
    }
    return Math.min(byWidth, availableH / shellH);
  });

  protected readonly scalePercent = computed(() => Math.round(this.scale() * 100));

  protected readonly scaledViewportWidth = computed(() => this.shellWidth() * this.scale());

  protected readonly scaledViewportHeight = computed(() => this.shellHeight() * this.scale());

  private readonly stageRef = viewChild<ElementRef<HTMLElement>>('stage');
  private readonly scaleFooterRef = viewChild<ElementRef<HTMLElement>>('scaleFooter');

  constructor() {
    let firstPresetEffect = true;
    effect((onCleanup) => {
      this.selectedDeviceId();
      this.deviceChrome();
      if (firstPresetEffect) {
        firstPresetEffect = false;
        return;
      }

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      this.presetTransitionActive.set(true);
      const timeoutId = window.setTimeout(() => {
        this.presetTransitionActive.set(false);
      }, PRESET_TRANSITION_MS);

      onCleanup(() => {
        window.clearTimeout(timeoutId);
      });
    });

    effect((onCleanup) => {
      const stageRef = this.stageRef();
      const footerRef = this.scaleFooterRef();
      if (!stageRef || !footerRef) {
        return;
      }
      const stageEl = stageRef.nativeElement;
      const footerEl = footerRef.nativeElement;
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const target = entry.target;
          if (target === stageEl) {
            this.stageWidth.set(entry.contentRect.width);
            this.stageHeight.set(entry.contentRect.height);
            const column = stageEl.querySelector('.preview-device-column');
            if (column instanceof HTMLElement) {
              const gap = Number.parseFloat(getComputedStyle(column).rowGap);
              this.columnGap.set(Number.isFinite(gap) ? gap : 0);
            }
          } else if (target === footerEl) {
            this.footerHeight.set(entry.contentRect.height);
          }
        }
      });
      observer.observe(stageEl);
      observer.observe(footerEl);
      onCleanup(() => observer.disconnect());
    });
  }

  protected onDeviceValueChange(raw: string | undefined): void {
    if (raw === undefined || !isDevicePresetId(raw)) {
      return;
    }
    this.selectedDeviceId.set(raw);
  }
}
